import { PlusOutlined } from '@ant-design/icons';
import { Button, Tabs, Form, Input, Checkbox, Select } from 'antd';
import classNames from 'classnames';
import { JobItem } from 'domains/workspaceDomain';
import { useUser } from 'hooks';
import React, { useEffect, useRef, useState } from 'react';
import { isSourcer } from 'utils/rbac';
import { RevxButton } from 'components/RevxButton';
import { useTranslation } from 'react-i18next';
import workspaceService from 'services/jobService';
import {
  JobRFQBuildQuoteItemsParams,
  JobRFQOrgItemViewer,
  JobRFQQuoteItemViewer,
  JobRFQViewer,
  RFQStatusEnum,
  RFQOrgStatusEnum,
} from 'domains/jobconfigDomain';
import { useMount, useClickAway } from 'ahooks';
import { useAppDispatch } from 'app/hooks';
import TabSelectSuppliers, { Rerender } from './components/TabSelectSuppliers';
import TabDetermineShipping from './components/TabDetermineShipping';
import TabResponses, { SubTableMainData } from './components/TabResponses';
import TabQuoteBuilder from './components/TabQuoteBuilder';
import TabSupplierResponseRecord from './components/TabSupplierResponseRecord';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { EMAIL_MAXLENGTH, LAST_NAME_MAXLENGTH } from 'utils/constants';
import { NameBadge } from 'features/Workspace/components/NameBadge';
import { clone } from 'lodash-es';
import workspaceDetailService from 'services/workspaceDetailService';
import { useOrgId, useOrg } from 'hooks/useOrg';
import { isCustomerOrg, isSaasOrg } from 'domains/orgDomain';
import './index.less';

export enum TabsKeys {
  supplier = '1',
  determine = '2',
  responses = '3',
  quote = '4',
  supplierResponse = '5',
  supplierResponseRecord = '6',
}

type ValueType = { value: string; label: string };

const MAX_DETERMINE_SHIPPING_LEN = 5;

const CommonRFQ = (jobData: JobItem) => {
  const data = jobData;
  const user = useUser();
  const currentOrgId = useOrgId();
  const currentOrg = useOrg();
  const [tabsActiveKey, setTabsActiveKey] = useState<TabsKeys>(
    TabsKeys.supplier
  );
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [rfqData, setRfqData] = useState<JobRFQViewer>();
  const [rfqSupplierData, setRfqSupplierData] = useState<JobRFQViewer>();
  const [initSupplierOrgItems, setInitSupplierOrgItems] =
    useState<JobRFQOrgItemViewer[]>();
  const [rfqDetermineData, setRfqDetermineData] = useState<JobRFQViewer>();
  const [rfqResponsesData, setRfqResponsesData] = useState<JobRFQViewer>();
  const [responsesToQuoteData, setResponsesToQuoteData] =
    useState<Array<SubTableMainData>>();
  const [rfqQuoteItem, setRfqQuoteItem] =
    useState<Array<JobRFQQuoteItemViewer>>();
  const [rfqSentPct, setRfqSentPct] =
    useState<Partial<Record<RFQOrgStatusEnum, number>>>();
  const [showShareQuoteModal, setShowShareQuoteModal] =
    useState<boolean>(false);
  const [shareQuoteFormErrorList, setShareQuoteFormErrorList] = useState<
    string[]
  >([]);
  const [sendQuoteLoading, setSendQuoteLoading] = useState<boolean>(false);
  const [nameBadgeList, setNameBadgeList] = useState<string[]>([]);
  const [selectSupplierValue, setSelectSupplierValue] = useState<ValueType>();
  const [supplierOrgList, setSupplierOrgList] =
    useState<{ label: string; value: number }[]>();
  const [determineDataLength, setDetermineDataLength] = useState<number>();
  const quoteModalRef = useRef<HTMLDivElement>(null);
  const quoteModalRef2 = useRef<HTMLButtonElement>(null);
  const supplierTableRef = useRef<Rerender>(null);
  const [shareQuoteForm] = Form.useForm();
  const isOwnRFQJob = data?.customerId === user?.userId;
  const isClosedRFQ =
    data?.rfqStatus === RFQStatusEnum.CLOSED ||
    data?.rfqStatus === RFQStatusEnum.COMPLETE ||
    rfqData?.rfqStatus === RFQStatusEnum.CLOSED ||
    rfqData?.rfqStatus === RFQStatusEnum.COMPLETE;

  useMount(async () => {
    await getJobRFQViewer();
  });

  useClickAway(() => {
    setShowShareQuoteModal(false);
  }, [quoteModalRef, quoteModalRef2]);

  useEffect(() => {
    if (rfqResponsesData) {
      const { orgItems } = rfqResponsesData;
      let responded = 0,
        declined = 0;
      const orgItemsLength = orgItems.length;
      orgItems.forEach(({ rfqOrgStatus }) => {
        switch (rfqOrgStatus) {
          case RFQOrgStatusEnum.IN_PROCESS:
            responded++;
            break;
          case RFQOrgStatusEnum.DECLINED:
            declined++;
            break;
        }
      });
      const percentageParse = (n: number) =>
        Math.round((n / orgItemsLength) * 100) || 0;
      const r = percentageParse(responded);
      const d = percentageParse(declined);
      const n = 100 - r - d;
      setRfqSentPct({
        [RFQOrgStatusEnum.IN_PROCESS]: r,
        [RFQOrgStatusEnum.DECLINED]: d,
        [RFQOrgStatusEnum.TO_REVIEW]: n,
      });
    }
  }, [rfqResponsesData]);

  useEffect(() => {
    updateTableSourceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfqData]);

  const getJobRFQViewer = async () => {
    try {
      dispatch.workspace.setState({
        jobListSpinning: true,
      });
      const rfqData = await getJobRFQData();
      await getJobRFQQuoteBuilderCacheData();
      await getSimpleOrgListHandler(rfqData);
    } catch {
    } finally {
      dispatch.workspace.setState({
        jobListSpinning: false,
      });
    }
  };

  const getJobRFQQuoteBuilderCacheData = async (jumpToResponses?: boolean) => {
    const quoteBuilderCacheData =
      await workspaceService.getRfqJobQuoteBuilderItems(data?.id);
    setRfqQuoteItem(quoteBuilderCacheData);
    if (jumpToResponses) {
      setTabsActiveKey(TabsKeys.responses);
    }
  };

  const getJobRFQData = async (tabKey?: TabsKeys): Promise<JobRFQViewer> => {
    await dispatch.app.showLoading();
    const v = await workspaceService.getJobRfqViewer(data?.id);
    setRfqData(v);
    await dispatch.app.hideLoading();
    if (tabKey) {
      setTabsActiveKey(tabKey);
    }
    return v;
  };

  const getSimpleOrgListHandler = async (rfqData: JobRFQViewer) => {
    const orgId =
      currentOrg &&
      rfqData?.orgId &&
      (isSaasOrg(currentOrg) || isCustomerOrg(currentOrg))
        ? rfqData.orgId
        : currentOrgId;
    const orgList = await workspaceDetailService.getMarketplaceOrgList(orgId);
    const filterOrgList = orgList.filter((i) => {
      return !rfqData.orgItems.find((u) => u.supplierOrgId === i.id);
    });
    setSupplierOrgList(
      filterOrgList.map((i) => {
        return {
          label: i.name,
          value: i.id,
        };
      })
    );
  };

  const updateRFQData = async () => {
    await getJobRFQData();
  };

  const selectSupplierUpdateData = async () => {
    await updateRFQData();
    setTabsActiveKey(TabsKeys.determine);
  };

  const determineShippingUpdateData = async () => {
    await updateRFQData();
  };

  const updateTableSourceData = () => {
    if (rfqData) {
      const supplierData = rfqData.orgItems.filter(
        (i) => i.rfqOrgStatus === RFQOrgStatusEnum.INIT
      );
      const determineData = rfqData.orgItems.filter(
        (i) => i.rfqOrgStatus === RFQOrgStatusEnum.DRAFT
      );
      const responsesData = rfqData.orgItems.filter(
        (i) =>
          i.rfqOrgStatus !== RFQOrgStatusEnum.INIT &&
          i.rfqOrgStatus !== RFQOrgStatusEnum.DRAFT
      );
      let draftLen = determineData.length || 0;
      responsesData.forEach((i) => {
        if (i.hasUpdateShippingDraft) {
          draftLen++;
        }
      });
      setDetermineDataLength(draftLen);
      setRfqSupplierData(clone({ ...rfqData, ...{ orgItems: supplierData } }));
      setInitSupplierOrgItems(supplierData);
      setRfqResponsesData({ ...rfqData, ...{ orgItems: responsesData } });
      setRfqDetermineData({ ...rfqData, ...{ orgItems: determineData } });
    }
  };

  const jumpToQuoteBuilderView = async () => {
    if (responsesToQuoteData) {
      await dispatch.app.showLoading();
      const sendParams: JobRFQBuildQuoteItemsParams[] =
        responsesToQuoteData?.map((i) => {
          const { orgId, qtySerialNumber } = i;
          return {
            orgId,
            qtySerialNumber,
          };
        });
      const quoteBuilderData =
        await workspaceService.sendRfqJobQuoteBuilderItems({
          jobId: data.id,
          sendParams,
        });
      setRfqQuoteItem(quoteBuilderData);
      setTabsActiveKey(TabsKeys.quote);
      await dispatch.app.hideLoading();
    }
  };

  const addNoEligibleOrg = async () => {
    if (selectSupplierValue?.value) {
      await dispatch.app.showLoading();
      await workspaceDetailService.addNoEligibleOrg(
        data.id,
        Number(selectSupplierValue.value)
      );
      await dispatch.app.hideLoading();
      await getJobRFQViewer();
      setSelectSupplierValue({ label: '', value: '' });
      supplierTableRef.current?.rerenderTable();
    }
  };

  const filterSupplierOrgHandler = (value: string) => {
    const v = value.toLowerCase();
    let orgItems: JobRFQOrgItemViewer[] | undefined;
    if (v) {
      orgItems = initSupplierOrgItems?.filter((i) =>
        i.supplierOrgName.toLowerCase().includes(v)
      );
    } else {
      orgItems = initSupplierOrgItems;
    }
    setRfqSupplierData(clone(Object.assign({}, rfqSupplierData, { orgItems })));
  };

  const tabsRightSlot = {
    right: (
      <div className="tabs-right-bar">
        <Select
          autoClearSearchValue={true}
          labelInValue={true}
          showSearch
          value={selectSupplierValue}
          options={supplierOrgList}
          optionFilterProp="label"
          placeholder="Don’t see the vendor you are looking for? Enter supplier name:"
          allowClear={true}
          onChange={(v: ValueType) => {
            setSelectSupplierValue(v);
            filterSupplierOrgHandler(v?.label || '');
          }}
          onSearch={filterSupplierOrgHandler}
        />
        <RevxButton
          brandPurple={true}
          className="add-supplier-btn"
          onClick={async () => {
            await addNoEligibleOrg();
          }}
        >
          Add Supplier
        </RevxButton>
      </div>
    ),
  };
  const tabsRightResponses = {
    right: (
      <div className="tabs-right-bar responses">
        <p>Select Suppliers and Quantities to Build Quote </p>
        <RevxButton
          type="ghost"
          className="view"
          disabled={responsesToQuoteData && responsesToQuoteData?.length < 1}
          onClick={jumpToQuoteBuilderView}
        >
          Build Quote
        </RevxButton>
      </div>
    ),
  };

  const tabsRightSlotQuote = {
    right: (
      <div className="tabs-right-bar quote">
        <div className="name-badge-wrap">
          {nameBadgeList.map((i, k) => (
            <NameBadge name={i} key={k} />
          ))}
        </div>
        <div className="tabs-right-bar__left">
          <Button
            ref={quoteModalRef2}
            className="share"
            shape="round"
            onClick={() => {
              setShowShareQuoteModal(!showShareQuoteModal);
            }}
          >
            <PlusOutlined />
            Share
          </Button>
        </div>
        <div className="tabs-right-bar__right">
          <RevxButton
            className="view"
            onClick={() => {
              if (!rfqData?.id) return;
              window.open(
                workspaceService.viewRfqJobQuote({
                  id: rfqData.id,
                })
              );
            }}
          >
            View PDF Quote
          </RevxButton>
        </div>
      </div>
    ),
  };

  const tableElMap: Record<string, { slot: React.ReactNode }> = {
    '1': {
      slot: tabsRightSlot,
    },
    '2': {
      slot: <></>,
    },
    '3': {
      slot: tabsRightResponses,
    },
    '4': {
      slot: tabsRightSlotQuote,
    },
    '5': {
      slot: <></>,
    },
    '6': {
      slot: <></>,
    },
  };

  useEffect(() => {
    if (!showShareQuoteModal) {
      shareQuoteForm.resetFields();
      setShareQuoteFormErrorList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showShareQuoteModal]);

  const sendShareQuoteHandler = () => {
    formErrorFinder();
    shareQuoteForm
      .validateFields()
      .then(async ({ email, firstName, lastName, quantity }) => {
        if (!rfqData) return;
        setSendQuoteLoading(true);
        const serialNumbers = quantity.map((i: string) => {
          const u = i.split('_');
          return {
            orgId: u[0],
            qtySerialNumber: u[1],
          };
        });
        await workspaceService.shareRfqJobQuote({
          rfqId: rfqData?.id,
          email,
          firstName,
          lastName,
          serialNumbers,
        });
        setNameBadgeList([...nameBadgeList, ...[`${firstName} ${lastName}`]]);
        setSendQuoteLoading(false);
        setShowShareQuoteModal(false);
      });
  };
  const formErrorFinder = () => {
    shareQuoteForm
      .validateFields()
      .then((r) => {
        setShareQuoteFormErrorList([]);
      })
      .catch((err) => {
        setShareQuoteFormErrorList(
          err.errorFields.filter(({ name }: { name: string[] }) => {
            return name.join().toLowerCase().indexOf('name') > 0;
          })
        );
      });
  };

  return (
    <div className="rfq-main">
      <div className="rfq-separate"></div>
      {isSourcer(user) && (
        <div className={classNames('workspace-item-detail-wrapper')}>
          <div className="workspace-item-tab-panel">
            <div className="rfq-container">
              <div className="rfq-header workspace-item-content-row">
                <h4 className="rfq-header__title">
                  {t('workspace.rfq.tablesTitle')}
                </h4>
                {!!rfqResponsesData?.orgItems.length && (
                  <>
                    <div className="rfq-header__percentage-title">
                      <p>
                        {t('workspace.rfq.tablesChartBar.title', {
                          count: rfqResponsesData?.orgItems.length,
                        })}
                      </p>
                    </div>
                    <div className="rfq-header__percentage">
                      <div className="offer-result-chart">
                        <div className="offer-result-chart-bar">
                          <div
                            style={{ width: `${rfqSentPct?.TO_REVIEW}%` }}
                            className="offer-result-chart-bar__item"
                          />
                          <div
                            style={{ width: `${rfqSentPct?.IN_PROCESS}%` }}
                            className="offer-result-chart-bar__item"
                          />
                          <div
                            style={{ width: `${rfqSentPct?.DECLINED}%` }}
                            className="offer-result-chart-bar__item"
                          />
                        </div>
                        <div className="offer-result-chart-legend">
                          <div className="offer-result-chart-legend-item">
                            <div className="offer-result-chart-legend-item-bullet sent" />
                            {rfqSentPct?.TO_REVIEW}% No Action
                          </div>
                          <div className="offer-result-chart-legend-item">
                            <div className="offer-result-chart-legend-item-bullet responded" />
                            {rfqSentPct?.IN_PROCESS}% Responded
                          </div>
                          <div className="offer-result-chart-legend-item">
                            <div className="offer-result-chart-legend-item-bullet declined" />
                            {rfqSentPct?.DECLINED}% Declined
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="rfq-table">
                <Tabs
                  activeKey={tabsActiveKey}
                  tabBarExtraContent={tableElMap[tabsActiveKey].slot}
                  onChange={(v: string) => {
                    setTabsActiveKey(v as TabsKeys);
                    setShowShareQuoteModal(false);
                  }}
                >
                  <Tabs.TabPane
                    tab={isClosedRFQ ? `Not Responses (${rfqSupplierData?.orgItems.length || 0})` : t('workspace.rfq.tabs.selectSuppliers', {
                      count: rfqSupplierData?.orgItems.length,
                    })}
                    key={TabsKeys.supplier}
                  >
                    <TabSelectSuppliers
                      ref={supplierTableRef}
                      data={rfqSupplierData}
                      determineDataLength={rfqDetermineData?.orgItems.length}
                      selectSupplierUpdateData={selectSupplierUpdateData}
                      readOnly={isClosedRFQ}
                    />
                  </Tabs.TabPane>
                  {!isClosedRFQ && <Tabs.TabPane
                    tab={t('workspace.rfq.tabs.determineShipping', {
                      count: determineDataLength,
                    })}
                    key={TabsKeys.determine}
                  >
                    <TabDetermineShipping
                      data={rfqDetermineData}
                      jobData={data}
                      determineShippingUpdateData={determineShippingUpdateData}
                      reloadQuoteDate={getJobRFQQuoteBuilderCacheData}
                    />
                  </Tabs.TabPane>}
                  <Tabs.TabPane
                    tab={t('workspace.rfq.tabs.responses', {
                      count: rfqResponsesData?.orgItems.length,
                    })}
                    key={TabsKeys.responses}
                  >
                    <TabResponses
                      data={rfqResponsesData}
                      jobData={data}
                      reloadRfqData={getJobRFQData}
                      updateQuoteDate={setResponsesToQuoteData}
                      canUpdateShipping={
                        !!(
                          rfqDetermineData &&
                          rfqDetermineData.orgItems.length <
                            MAX_DETERMINE_SHIPPING_LEN
                        )
                      }
                      reloadQuoteDate={getJobRFQQuoteBuilderCacheData}
                    />
                  </Tabs.TabPane>
                  {rfqQuoteItem?.length && (
                    <Tabs.TabPane
                      tab={t('workspace.rfq.tabs.quoteBuilder')}
                      key={TabsKeys.quote}
                    >
                      <TabQuoteBuilder
                        isOwnRFQJob={isOwnRFQJob}
                        data={rfqQuoteItem}
                        updateData={setRfqQuoteItem}
                        jobId={data?.id}
                      />
                    </Tabs.TabPane>
                  )}
                  {/* <Tabs.TabPane
                    tab="Supplier Response"
                    key={TabsKeys.supplierResponse}
                  >
                    <TabSupplierResponse
                      rfqId={rfqData?.id || 0}
                      supplierOrgItems={rfqData?.orgItems}
                    />
                  </Tabs.TabPane> */}
                  <Tabs.TabPane
                    tab="Supplier Response Record"
                    key={TabsKeys.supplierResponseRecord}
                  >
                    <TabSupplierResponseRecord rfqId={rfqData?.id} />
                  </Tabs.TabPane>
                </Tabs>
                {showShareQuoteModal && (
                  <div ref={quoteModalRef} className="share-quote-modal">
                    <div className="title">share a quote</div>
                    <Form
                      form={shareQuoteForm}
                      onValuesChange={formErrorFinder}
                    >
                      <RevxFlexLayout className="form-wrap">
                        <RevxFlexLayout
                          className={classNames('name-input ant-input', {
                            'quote-item-error':
                              shareQuoteFormErrorList?.length > 0,
                          })}
                        >
                          <Form.Item
                            name="firstName"
                            rules={[
                              {
                                required: true,
                              },
                            ]}
                          >
                            <Input
                              maxLength={LAST_NAME_MAXLENGTH}
                              placeholder="First Name"
                            />
                          </Form.Item>
                          <Form.Item
                            name="lastName"
                            rules={[
                              {
                                required: true,
                              },
                            ]}
                          >
                            <Input
                              maxLength={LAST_NAME_MAXLENGTH}
                              placeholder="Last Name"
                            />
                          </Form.Item>
                        </RevxFlexLayout>
                        <RevxFlexLayout className="email-input">
                          <Form.Item
                            name="email"
                            rules={[
                              {
                                required: true,
                              },
                              {
                                type: 'email',
                                message: 'Please enter valid Email',
                              },
                            ]}
                          >
                            <Input
                              maxLength={EMAIL_MAXLENGTH}
                              placeholder="Email"
                            />
                          </Form.Item>
                        </RevxFlexLayout>
                      </RevxFlexLayout>
                      <RevxFlexLayout
                        justify="justify-end"
                        className="checkbox-item"
                      >
                        <Form.Item
                          name="quantity"
                          rules={[
                            {
                              required: true,
                              message: 'Please select quantity',
                            },
                          ]}
                        >
                          <Checkbox.Group>
                            {rfqQuoteItem?.map((i, index) => {
                              return (
                                <RevxFlexLayout>
                                  <Checkbox
                                    value={`${i.orgId}_${i.qtySerialNumber}_${index}`}
                                    key={index}
                                  />
                                  <span className="quantity">{i.quantity}</span>
                                </RevxFlexLayout>
                              );
                            })}
                          </Checkbox.Group>
                        </Form.Item>
                      </RevxFlexLayout>
                    </Form>
                    <RevxFlexLayout justify="justify-end">
                      <RevxButton
                        loading={sendQuoteLoading}
                        className="send-btn"
                        type="primary"
                        onClick={sendShareQuoteHandler}
                      >
                        Send
                      </RevxButton>
                    </RevxFlexLayout>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommonRFQ;
